import { api } from '@/lib/api/client';
import { ApiError } from '@/lib/api/errors';
import type {
  XyComponent,
  XyStep,
  XySurvey,
  XySurveyResponseBulk,
} from '@/lib/api/types';

function assertNotDeleted<T extends { status?: string }>(entity: T, kind: string): T {
  if (entity?.status === 'Deleted') {
    throw new ApiError({ message: `${kind} has been deleted`, status: 404, code: 'NOT_FOUND' });
  }
  return entity;
}

/**
 * Surveys API.
 *
 * Backend routes (ox-platform xurvey module):
 *   GET    /xy-xurvey                         → list (filter: productType, isTemplate, companyId)
 *   GET    /xy-xurvey/:id                     → single
 *   POST   /xy-xurvey                         → create (meta only)
 *   PUT    /xy-xurvey/:id                     → update (PUT, not PATCH)
 *   DELETE /xy-xurvey/:id                     → delete
 *   POST   /xy-xurvey/with-step               → create survey + steps + components atomically
 *   GET    /xy-xurvey/:id/preview             → preview (no station context)
 *   POST   /xy-xurvey/:id/clone               → clone for a company
 *   POST   /xy-xurvey/:id/submit              → JWT-authenticated bulk submit
 *   DELETE /xy-xurvey/:id/steps               → drop all steps
 *   POST   /xy-xurvey/:id/send-invites        → send S1→S2 invitations
 *
 * `templatesOnly` / `isTemplate` filters are passed via the standard `filter`
 * JSON param. See backend xy-surveys.controller.ts.
 */
export interface SurveyListFilter {
  /** Survey product family. Front-end consumers should pass 'xotski'. */
  productType?: 'xotski' | 'xamplin' | 'xeckin' | 'rs1' | 'rs2';
  /** Only templates (`isTemplate=true`). */
  isTemplate?: boolean;
  companyId?: number;
  [k: string]: unknown;
}

export interface CreateWithStepsInput
  extends Omit<Partial<XySurvey>, 'steps' | 'id'> {
  steps: Array<
    Omit<XyStep, 'id' | 'surveyId' | 'createdAt' | 'updatedAt'> & {
      components: Array<
        Omit<XyComponent, 'id' | 'stepId' | 'createdAt' | 'updatedAt'>
      >;
    }
  >;
}

export const surveysApi = {
  list: (filter?: SurveyListFilter) => api.list<XySurvey>('/xy-xurvey', { filter }),
  listTemplates: (productType: SurveyListFilter['productType'] = 'xotski') =>
    api.list<XySurvey>('/xy-xurvey', { filter: { isTemplate: true, productType } }),

  get: (id: number) =>
    api.get<XySurvey>(`/xy-xurvey/${id}`).then((s) => assertNotDeleted(s, 'Survey')),
  /**
   * Returns the survey enriched with its steps (each with components),
   * brand-level branding, and optionally cached `surveysObject` snapshot.
   * Distinct from `getObject()` which is the station-scoped public render.
   */
  preview: (id: number) =>
    api.get<XySurvey & { steps?: (XyStep & { components: XyComponent[] })[] }>(
      `/xy-xurvey/${id}/preview`,
    ),

  create: (input: Partial<XySurvey>) => api.post<XySurvey>('/xy-xurvey', input),
  createWithSteps: (input: CreateWithStepsInput) =>
    api.post<XySurvey>('/xy-xurvey/with-step', input),

  update: (id: number, patch: Partial<XySurvey>) =>
    api.put<XySurvey>(`/xy-xurvey/${id}`, patch),
  // Soft delete — see eventsApi.
  remove: (id: number) =>
    api.put<XySurvey>(`/xy-xurvey/${id}`, { status: 'Deleted' }),

  clone: (id: number) => api.post<XySurvey>(`/xy-xurvey/${id}/clone`),
  deleteAllSteps: (id: number) =>
    api.delete<{ ok: true; id: number }>(`/xy-xurvey/${id}/steps`),

  /** Authenticated submit (admin path). Public bulk is in publicSurveyApi. */
  submit: (id: number, payload: XySurveyResponseBulk) =>
    api.post<{ ok: true }>(`/xy-xurvey/${id}/submit`, payload),

  sendInvites: (id: number, payload: { emails: string[] }) =>
    api.post<{ ok: true; sent: number }>(`/xy-xurvey/${id}/send-invites`, payload),
};

/**
 * Individual step CRUD. Used by SurveyBuilder for fine-grained edits without
 * rewriting the whole survey on every keystroke.
 */
export const stepsApi = {
  list: (filter?: { surveyId?: number }) => api.list<XyStep>('/xy-step', { filter }),
  get: (id: number) =>
    api.get<XyStep>(`/xy-step/${id}`).then((s) => assertNotDeleted(s, 'Step')),
  create: (input: Partial<XyStep>) => api.post<XyStep>('/xy-step', input),
  update: (id: number, patch: Partial<XyStep>) =>
    api.put<XyStep>(`/xy-step/${id}`, patch),
  remove: (id: number) => api.put<XyStep>(`/xy-step/${id}`, { status: 'Deleted' }),
};

/** Individual component CRUD. Same rationale as stepsApi. */
export const componentsApi = {
  list: (filter?: { stepId?: number }) =>
    api.list<XyComponent>('/xy-component', { filter }),
  get: (id: number) =>
    api.get<XyComponent>(`/xy-component/${id}`).then((c) => assertNotDeleted(c, 'Component')),
  create: (input: Partial<XyComponent>) =>
    api.post<XyComponent>('/xy-component', input),
  update: (id: number, patch: Partial<XyComponent>) =>
    api.put<XyComponent>(`/xy-component/${id}`, patch),
  remove: (id: number) =>
    api.put<XyComponent>(`/xy-component/${id}`, { status: 'Deleted' }),
};
